import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE = "https://1099er.co";
const BASE = "1099er";

/**
 * Per-route SEO metadata. Keeps title, description, canonical, and Open Graph
 * URL in sync with the currently rendered page. All content is derived from
 * what actually appears on each page — no invented claims.
 */
const ROUTES = {
    "/": {
        title: `${BASE} — Independent-line opportunities for orthopedic and biologics reps`,
        description:
            "1099er connects experienced orthopedic and biologics medical device reps with complementary independent-line product opportunities. Launch product: Induce XT bone allograft.",
        canonical: `${SITE}/`,
        robots: "index,follow",
    },
    "/for-reps": {
        title: `Schedule a Confidential Call · ${BASE}`,
        description:
            "Add a complementary orthopedic or biologics product line to what you already carry. Apply privately to join the 1099er network.",
        canonical: `${SITE}/for-reps`,
        robots: "index,follow",
    },
    "/for-surgeons": {
        title: `For Surgeons · ${BASE}`,
        description:
            "1099er helps surgeons keep working with the reps they already trust by letting those reps add complementary, non-conflicting product lines like Induce XT.",
        canonical: `${SITE}/for-surgeons`,
        robots: "index,follow",
    },
    "/inducext": {
        title: `Induce XT — bone allograft with NMP technology · ${BASE}`,
        description:
            "Induce XT is a human bone allograft with multiple native growth factors. Its proprietary Natural Matrix Protein (NMP) processing is designed to increase the bioavailability of growth factors for foot and ankle bone grafting.",
        canonical: `${SITE}/inducext`,
        robots: "index,follow",
    },
    "/contact": {
        title: `Contact · ${BASE}`,
        description:
            "Get in touch with 1099er about independent-line rep opportunities, product fit, or the Induce XT nationwide launch.",
        canonical: `${SITE}/contact`,
        robots: "index,follow",
    },
    "/faq": {
        title: `FAQ · ${BASE}`,
        description:
            "Answers on how 1099er works — territories, contracting, commissions, confidentiality, and next steps for medical device reps.",
        canonical: `${SITE}/faq`,
        robots: "index,follow",
    },
    "/about": {
        title: `About · ${BASE}`,
        description:
            "1099er is a private network connecting medical technology companies with experienced sales representatives who already have the specialty, account, and physician relationships those companies want to reach.",
        canonical: `${SITE}/about`,
        robots: "index,follow",
    },
    "/admin": {
        title: `Admin · ${BASE}`,
        description: "",
        canonical: `${SITE}/admin`,
        robots: "noindex,nofollow",
    },
    "/products": {
        title: `Product Opportunities · ${BASE}`,
        description: "",
        canonical: `${SITE}/products`,
        robots: "noindex,nofollow",
    },
};

const FALLBACK = ROUTES["/"];

function upsertMeta(selector, attrName, attrValue, contentAttr, contentValue) {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
    }
    el.setAttribute(contentAttr, contentValue);
}

function upsertLink(rel, href) {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

export default function TitleManager() {
    const { pathname } = useLocation();

    useEffect(() => {
        const meta = ROUTES[pathname] || FALLBACK;

        document.title = meta.title;
        upsertMeta('meta[name="description"]', "name", "description", "content", meta.description);
        upsertMeta('meta[name="robots"]', "name", "robots", "content", meta.robots);
        upsertLink("canonical", meta.canonical);

        upsertMeta('meta[property="og:title"]', "property", "og:title", "content", meta.title);
        upsertMeta('meta[property="og:description"]', "property", "og:description", "content", meta.description);
        upsertMeta('meta[property="og:url"]', "property", "og:url", "content", meta.canonical);
        upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", "content", meta.title);
        upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", "content", meta.description);
    }, [pathname]);

    return null;
}
